/**
 * Centralized API client.
 *
 * Replaces postDataActions.js and makeFetch from the legacy codebase.
 * - Configurable base URL
 * - Bearer from setAuthTokenProvider when set; otherwise ?userId= from setCurrentUserIdProvider (dev)
 * - Consistent error handling and response unwrapping
 * - Optional `{ signal }` on all methods — pass AbortController.signal so navigation cancels in-flight requests
 */

let getAuthToken = () => null;
let getCurrentUserId = () => null;

export const setAuthTokenProvider = fn => {
    getAuthToken = fn;
};

/** Used to append ?userId= when no Bearer token (local dev until backend mints app JWT). */
export const setCurrentUserIdProvider = fn => {
    getCurrentUserId = fn;
};

const getBaseUrl = () => {
    if (typeof window !== 'undefined' && window.__PRA_API_BASE__) {
        return window.__PRA_API_BASE__;
    }
    return import.meta.env.VITE_API_URL || '/api';
};

const buildHeaders = () => {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    const token = getAuthToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return headers;
};

const buildUrl = (endpoint, query = {}) => {
    const base = getBaseUrl();
    const params = new URLSearchParams();

    let path = endpoint;
    const queryIndex = String(endpoint).indexOf('?');
    if (queryIndex !== -1) {
        path = endpoint.slice(0, queryIndex);
        const existingParams = new URLSearchParams(endpoint.slice(queryIndex + 1));
        existingParams.forEach((value, key) => {
            params.set(key, value);
        });
    }

    Object.entries(query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.set(key, String(value));
        }
    });

    const token = getAuthToken();
    const userId = getCurrentUserId();
    if (!token && userId && params.get('userId') == null) {
        params.set('userId', String(userId));
    }

    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const queryString = params.toString();
    return queryString ? `${base}/${normalizedPath}?${queryString}` : `${base}/${normalizedPath}`;
};

export const apiGet = async (endpoint, query, options = {}) => {
    const url = buildUrl(endpoint, query);
    const response = await fetch(url, {
        headers: buildHeaders(),
        signal: options.signal,
    });
    if (!response.ok) {
        console.error(`[apiGet] HTTP error ${endpoint}`, response.status);
    }
    return response.json();
};

export const apiPost = async (endpoint, body, options = {}) => {
    const url = buildUrl(endpoint);
    const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(body),
        signal: options.signal,
    });
    if (!response.ok) {
        console.error(`[apiPost] HTTP error ${endpoint}`, response.status);
    }
    return response.json();
};

export const apiPatch = async (endpoint, body, options = {}) => {
    const url = buildUrl(endpoint);
    const response = await fetch(url, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify(body),
        signal: options.signal,
    });
    if (!response.ok) {
        console.error(`[apiPatch] HTTP error ${endpoint}`, response.status);
    }
    return response.json();
};

export const apiDelete = async (endpoint, query, options = {}) => {
    const url = buildUrl(endpoint, query);
    const response = await fetch(url, {
        method: 'DELETE',
        headers: buildHeaders(),
        signal: options.signal,
    });
    if (!response.ok) {
        console.error(`[apiDelete] HTTP error ${endpoint}`, response.status);
    }
    return response.json();
};
